import React from 'react';
import { useTranslation } from 'react-i18next';

const DiscountSelectionModal = ({ isOpen, onClose, discounts, selectedDiscountId, onSelect, isItemDiscount = false }) => {
  const { t, i18n } = useTranslation();

  if (!isOpen) return null;

  const handleSelect = (discountId) => {
    onSelect(discountId);
    onClose();
  };

  const getTypeIcon = (type) => {
    if (type === 'percentage') {
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    }
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <svg className="w-6 h-6 text-white mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            <h2 className="text-2xl font-bold text-white">
              {isItemDiscount ? t('discounts.selectItemDiscount') : t('discounts.selectOrderDiscount')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* No Discount Option */}
          <div
            onClick={() => handleSelect('')}
            className={`mb-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${
              !selectedDiscountId
                ? 'border-orange-500 bg-orange-50'
                : 'border-gray-200 hover:border-orange-300 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`w-6 h-6 rounded-full border-2 mr-3 flex items-center justify-center ${
                  !selectedDiscountId ? 'border-orange-500 bg-orange-500' : 'border-gray-300'
                }`}>
                  {!selectedDiscountId && (
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{t('discounts.noDiscount')}</p>
                  <p className="text-sm text-gray-500">
                    {isItemDiscount ? t('discounts.noItemDiscountDescription') : t('discounts.noOrderDiscountDescription')}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900">0%</p>
              </div>
            </div>
          </div>

          {/* Discount Options Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {discounts.map((discount) => (
              <div
                key={discount.id}
                onClick={() => handleSelect(discount.id)}
                className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  selectedDiscountId === discount.id
                    ? 'border-orange-500 bg-orange-50 shadow-lg'
                    : 'border-gray-200 hover:border-orange-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start flex-1">
                    <div className={`w-6 h-6 rounded-full border-2 mr-3 flex items-center justify-center flex-shrink-0 ${
                      selectedDiscountId === discount.id ? 'border-orange-500 bg-orange-500' : 'border-gray-300'
                    }`}>
                      {selectedDiscountId === discount.id && (
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-gray-900 mb-2">
                        {i18n.language === 'ar' && discount.name_ar ? discount.name_ar : discount.name}
                      </h3>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          discount.type === 'percentage'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {getTypeIcon(discount.type)}
                          {discount.type === 'percentage' ? t('discounts.percentage') : t('discounts.fixedAmount')}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right ml-3">
                    <p className="text-2xl font-bold text-orange-600">
                      {discount.type === 'percentage'
                        ? `${parseFloat(discount.value)}%`
                        : `${t('common.currency')} ${parseFloat(discount.value).toFixed(2)}`
                      }
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {discount.type === 'percentage' ? t('discounts.off') : t('salesOrders.discount')}
                    </p>
                  </div>
                </div>

                {discount.description && (
                  <p className="text-sm text-gray-600 mt-2 pl-9">
                    {discount.description}
                  </p>
                )}

                {/* Show both languages if available */}
                {discount.name_ar && i18n.language !== 'ar' && (
                  <p className="text-xs text-gray-500 mt-2 pl-9" dir="rtl">
                    {discount.name_ar}
                  </p>
                )}

                {/* Preview calculation for percentage discounts */}
                {/* {discount.type === 'percentage' && (
                  <div className="mt-3 pl-9 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500">
                      Example: {t('common.currency')} 100.00 → {t('common.currency')} {(100 - (100 * discount.value / 100)).toFixed(2)}
                    </p>
                  </div>
                )} */}
              </div>
            ))}
          </div>

          {discounts.length === 0 && (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              <p className="text-gray-500">{t('discounts.noDiscountsAvailable')}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end border-t">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DiscountSelectionModal;
